import Swiper, { Grid, Navigation } from 'swiper';

const heightControllers = new WeakMap();

const getGridRows = (slidesCount) => {
  if (slidesCount <= 3) {
    return 1;
  }

  if (slidesCount <= 6) {
    return 2;
  }

  if (slidesCount >= 13) {
    return 4;
  }

  return 3;
};

const createHeightController = (swiperEl, swiper) => {
  let resizeTimeout = null;
  let resizeFrame = null;

  const setStableHeight = () => {
    if (swiper.destroyed) {
      return;
    }

    const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
    const items = Array.from(swiperEl.querySelectorAll('.advantage-sec__item'));

    if (!wrapperEl || !slides.length || !items.length) {
      return;
    }

    swiperEl.style.height = 'auto';
    wrapperEl.style.height = 'auto';
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    const maxItemHeight = Math.max(...items.map((item) => item.offsetHeight));
    const gap = Number.parseFloat(swiper.params.spaceBetween) || 0;
    const rows = swiper.params.grid.rows;
    const slideHeight = Math.ceil(maxItemHeight + 8 / rows);
    const height = slideHeight * rows + gap * (rows - 1);

    swiperEl.style.removeProperty('height');
    wrapperEl.style.removeProperty('height');
    slides.forEach((slide) => {
      slide.style.removeProperty('height');
    });
    swiperEl.style.setProperty('--advantage-swiper-height', `${height}px`);
    swiperEl.style.setProperty('--advantage-swiper-gap', `${gap}px`);
    swiperEl.style.setProperty('--advantage-swiper-slide-height', `${slideHeight}px`);
    swiper.update();
  };

  const requestStableHeight = () => {
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      setStableHeight();
    });
  };

  const onResize = () => {
    if (resizeTimeout) {
      return;
    }

    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      requestStableHeight();
    }, 150);
  };

  requestStableHeight();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(requestStableHeight).catch(requestStableHeight);
  } else {
    window.addEventListener('load', requestStableHeight, { once: true });
  }

  return { requestStableHeight };
};

const initAdvantageSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.advantage-swiper-wr');

  wrappers.forEach((wrapper) => {
    const swiperEl = wrapper.querySelector('.swiper');

    if (!swiperEl) {
      return;
    }

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;

    if (!slidesCount) {
      return;
    }

    const rows = getGridRows(slidesCount);

    if (swiperEl.swiper) {
      if (swiperEl.swiper.params.grid?.rows === rows) {
        heightControllers.get(wrapper)?.requestStableHeight();
        return;
      }

      swiperEl.swiper.destroy(true, true);
    }

    const nextEl = wrapper.querySelector('.swiper-button-next');
    const prevEl = wrapper.querySelector('.swiper-button-prev');
    const swiper = new Swiper(swiperEl, {
      modules: [Grid, Navigation],
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 12,
      speed: 500,
      threshold: 10,
      watchOverflow: true,
      grid: {
        rows,
        fill: 'column',
      },
      navigation: nextEl && prevEl ? {
        nextEl,
        prevEl,
      } : undefined,
      breakpoints: {
        552: {
          slidesPerView: 2,
          spaceBetween: 12,
          grid: {
            rows,
            fill: 'column',
          },
        },
        641: {
          slidesPerView: 3,
          spaceBetween: 16,
          grid: {
            rows,
            fill: 'column',
          },
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 18,
          grid: {
            rows,
            fill: 'column',
          },
        },
        820: {
          slidesPerView: 3,
          spaceBetween: 20,
          grid: {
            rows,
            fill: 'column',
          },
        },
        951: {
          slidesPerView: 3,
          spaceBetween: 22,
          grid: {
            rows,
            fill: 'column',
          },
        },
        1101: {
          slidesPerView: 3,
          spaceBetween: 24,
          grid: {
            rows,
            fill: 'column',
          },
        },
      },
    });

    swiper.params.grid.rows = rows;
    swiper.params.grid.fill = 'column';
    swiper.originalParams.grid.rows = rows;
    swiper.originalParams.grid.fill = 'column';
    swiper.update();

    heightControllers.set(wrapper, createHeightController(swiperEl, swiper));
  });
};

initAdvantageSwiper();
window.initAdvantageSwiper = initAdvantageSwiper;
